/* ==========================================================================
   sdp-analytics.js — GA4 tracking for the Stress Digital Product templates
   (page.stress-digital-product, the landing page, and
    page.stress-digital-program, the program reader).

   Every event goes through one funnel — SDPTrack() — with a single stable
   schema, so GA4/GTM always read the SAME universal parameters whatever the
   event fired:

     event          sdp_<name>     (GA4 event name / GTM trigger)
     sdp_template   which template  (product | program)
     sdp_section    section name    (offer, announce, faq, reader, …)
     sdp_action     semantic action (cta_click, day_open, plan_select, …)
     sdp_label      human label     (button text, day title, …)
     sdp_week       week number     (reader only)
     sdp_day        day number      (reader only)
     sdp_value      numeric value   (progress %, unlocked days, …)

   Events are pushed to BOTH window.dataLayer (GTM-MMPDQSW) AND gtag() (GA4
   G-Z8QLMDN801), so they land whether or not a GTM tag is wired for them.
   NOTE: if a GTM tag is later built forwarding sdp_ events to the SAME GA4
   property, drop the gtag() call below to avoid double-counting.

   Nothing fires in the theme editor — that would pollute GA4 with merchant
   clicks. Append ?sdp_debug=1 to log every event to the console instead.

   Markup hooks (no JS needed for the common cases):
     data-sdp-track="<action>"   fire on click
     data-sdp-event="<action>"   fire once on load
     data-sdp-section="<name>"   overrides the auto-detected section
     data-sdp-label="<text>"     overrides the element's own text
     data-sdp-value="<number>"
   Section-in-view is automatic: any `section-sdp-*` wrapper reports once.
   ========================================================================== */
(function () {
  'use strict';

  var DESIGN_MODE = !!(window.Shopify && window.Shopify.designMode);
  var DEBUG = (window.location.search || '').indexOf('sdp_debug') > -1;
  var fired = {};

  function template() {
    var c = document.body ? document.body.className : '';
    if (c.indexOf('template-page-stress-digital-program') > -1) return 'program';
    if (c.indexOf('template-page-stress-digital-product') > -1) return 'product';
    return 'unknown';
  }

  // "shopify-section-…" wrapper carries `section-sdp-<name>` from the schema.
  function sectionOf(el) {
    var node = el;
    while (node && node !== document.body) {
      var cls = node.className;
      if (typeof cls === 'string') {
        var m = /(?:^|\s)section-sdp-([a-z0-9-]+)/.exec(cls);
        if (m) return m[1];
      }
      node = node.parentNode;
    }
    return null;
  }

  function clean(s) {
    return String(s == null ? '' : s).replace(/\s+/g, ' ').trim().slice(0, 100);
  }

  function track(name, params) {
    if (DESIGN_MODE) return;
    params = params || {};
    var payload = {
      sdp_template: template(),
      sdp_section: params.section || null,
      sdp_action: params.action || name,
      sdp_label: params.label ? clean(params.label) : null,
      sdp_week: params.week != null ? params.week : null,
      sdp_day: params.day != null ? params.day : null,
      sdp_value: params.value != null ? params.value : null
    };
    var eventName = 'sdp_' + name;

    try {
      window.dataLayer = window.dataLayer || [];
      var obj = { event: eventName };
      for (var k in payload) {
        if (payload.hasOwnProperty(k)) obj[k] = payload[k];
      }
      window.dataLayer.push(obj);
    } catch (e) {}

    try {
      if (typeof window.gtag === 'function') window.gtag('event', eventName, payload);
    } catch (e) {}

    if (DEBUG) {
      try { console.log('[sdp] ' + eventName, payload); } catch (e) {}
    }
  }

  // Fire at most once per page for a given event+key (views, milestones).
  function trackOnce(key, name, params) {
    if (fired[key]) return;
    fired[key] = true;
    track(name, params);
  }

  window.SDPTrack = track;
  window.SDPTrackOnce = trackOnce;

  function bindClicks(scope) {
    var nodes = scope.querySelectorAll('[data-sdp-track]');
    for (var i = 0; i < nodes.length; i++) {
      (function (el) {
        if (el.getAttribute('data-sdp-bound')) return;
        el.setAttribute('data-sdp-bound', '1');
        el.addEventListener('click', function () {
          track(el.getAttribute('data-sdp-track'), {
            section: el.getAttribute('data-sdp-section') || sectionOf(el),
            action: el.getAttribute('data-sdp-track'),
            label: el.getAttribute('data-sdp-label') || el.textContent,
            value: el.getAttribute('data-sdp-value')
          });
        });
      })(nodes[i]);
    }
  }

  // Accordions (the FAQ). `toggle` so a close never counts as an open, and once
  // per question so re-opening the same row doesn't inflate the numbers.
  function bindAccordions(scope) {
    var items = scope.querySelectorAll('[class*="section-sdp-"] details');
    for (var i = 0; i < items.length; i++) {
      (function (el) {
        if (el.getAttribute('data-sdp-bound')) return;
        el.setAttribute('data-sdp-bound', '1');
        el.addEventListener('toggle', function () {
          if (!el.open) return;
          var summary = el.querySelector('summary');
          var label = clean(summary ? summary.textContent : '');
          trackOnce('faq:' + label, 'faq_open', {
            section: sectionOf(el),
            action: 'faq_open',
            label: label
          });
        });
      })(items[i]);
    }
  }

  function fireOnLoadEvents(scope) {
    var nodes = scope.querySelectorAll('[data-sdp-event]');
    for (var i = 0; i < nodes.length; i++) {
      var el = nodes[i];
      var name = el.getAttribute('data-sdp-event');
      trackOnce('load:' + name, name, {
        section: el.getAttribute('data-sdp-section') || sectionOf(el),
        label: el.getAttribute('data-sdp-label'),
        value: el.getAttribute('data-sdp-value')
      });
    }
  }

  // Section impressions — one event the first time each SDP section is 50%
  // visible. Skipped entirely where IntersectionObserver is unavailable.
  function observeSections() {
    if (!('IntersectionObserver' in window)) return;
    var sections = document.querySelectorAll('[class*="section-sdp-"]');
    if (!sections.length) return;
    var io = new IntersectionObserver(function (entries) {
      for (var i = 0; i < entries.length; i++) {
        var e = entries[i];
        if (!e.isIntersecting) continue;
        var name = sectionOf(e.target);
        io.unobserve(e.target);
        if (!name) continue;
        trackOnce('view:' + name, 'section_view', { section: name, action: 'section_view' });
      }
    }, { threshold: 0.5 });
    for (var i = 0; i < sections.length; i++) io.observe(sections[i]);
  }

  function boot() {
    bindClicks(document);
    bindAccordions(document);
    fireOnLoadEvents(document);
    observeSections();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  // Theme editor re-renders a section: re-bind the new markup.
  document.addEventListener('shopify:section:load', function (e) {
    var scope = e.target || document;
    bindClicks(scope);
    bindAccordions(scope);
  });
})();
